import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { OUTPUT_TYPES, OUTPUT_TYPE_LABELS } from "@production-hub/shared";
import type { BrokerOutput, OutputType } from "@production-hub/shared";

const outputSchema = z.object({
  name: z.string().min(1, "Name is required"),
  output_type: z.enum(OUTPUT_TYPES),
  enabled: z.boolean().default(true),
  sort_order: z.coerce.number().int().default(0),
  // RTMP fields
  rtmpUrl: z.string().optional(),
  streamKey: z.string().optional(),
  // SRT fields
  srtHost: z.string().optional(),
  srtPort: z.coerce.number().int().positive().optional(),
  srtMode: z.string().optional(),
  srtLatency: z.coerce.number().int().optional(),
  // YouTube fields
  youtubeRtmpUrl: z.string().optional(),
  youtubeStreamKey: z.string().optional(),
  broadcastId: z.string().optional(),
  youtubeApiKey: z.string().optional(),
});

type OutputFormValues = z.infer<typeof outputSchema>;

interface BrokerOutputFormProps {
  output?: BrokerOutput;
  onSubmit: (data: {
    name: string;
    output_type: OutputType;
    config: string | null;
    enabled: boolean;
    sort_order: number;
  }) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export const BrokerOutputForm = ({
  output,
  onSubmit,
  onCancel,
  isLoading,
}: BrokerOutputFormProps) => {
  const existingConfig =
    typeof output?.config === "string"
      ? JSON.parse(output.config)
      : output?.config || {};

  const form = useForm<OutputFormValues>({
    resolver: zodResolver(outputSchema),
    defaultValues: {
      name: output?.name ?? "",
      output_type: (output?.output_type as OutputType) ?? "rtmp",
      enabled: output?.enabled ?? true,
      sort_order: output?.sort_order ?? 0,
      rtmpUrl: existingConfig.url ?? "",
      streamKey: existingConfig.streamKey ?? "",
      srtHost: existingConfig.host ?? "",
      srtPort: existingConfig.port ?? undefined,
      srtMode: existingConfig.mode ?? "caller",
      srtLatency: existingConfig.latency ?? 200,
      youtubeRtmpUrl: existingConfig.rtmpUrl ?? "",
      youtubeStreamKey: existingConfig.streamKey ?? "",
      broadcastId: existingConfig.broadcastId ?? "",
      youtubeApiKey: existingConfig.youtubeApiKey ?? "",
    },
  });

  const outputType = form.watch("output_type");

  const handleSubmit = (values: OutputFormValues) => {
    let config: Record<string, unknown> = {};

    if (values.output_type === "rtmp") {
      config = { url: values.rtmpUrl, streamKey: values.streamKey };
    } else if (values.output_type === "srt") {
      config = {
        host: values.srtHost,
        port: values.srtPort,
        mode: values.srtMode,
        latency: values.srtLatency,
      };
    } else if (values.output_type === "youtube_live") {
      config = {
        rtmpUrl: values.youtubeRtmpUrl,
        streamKey: values.youtubeStreamKey,
        broadcastId: values.broadcastId,
        youtubeApiKey: values.youtubeApiKey,
      };
    }

    onSubmit({
      name: values.name,
      output_type: values.output_type,
      config: JSON.stringify(config),
      enabled: values.enabled,
      sort_order: values.sort_order,
    });
  };

  return (
    <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="name">Name</Label>
        <Input
          id="name"
          placeholder="YouTube Live"
          {...form.register("name")}
        />
        {form.formState.errors.name && (
          <p className="text-sm text-destructive">
            {form.formState.errors.name.message}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label>Output Type</Label>
        <Select
          value={outputType}
          onValueChange={(v) => form.setValue("output_type", v as OutputType)}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {OUTPUT_TYPES.map((type) => (
              <SelectItem key={type} value={type}>
                {OUTPUT_TYPE_LABELS[type]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {outputType === "rtmp" && (
        <>
          <div className="space-y-2">
            <Label htmlFor="rtmpUrl">RTMP URL</Label>
            <Input
              id="rtmpUrl"
              placeholder="rtmp://a.rtmp.youtube.com/live2"
              {...form.register("rtmpUrl")}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="streamKey">Stream Key</Label>
            <Input
              id="streamKey"
              type="password"
              {...form.register("streamKey")}
            />
          </div>
        </>
      )}

      {outputType === "srt" && (
        <>
          <div className="space-y-2">
            <Label htmlFor="srtHost">Host</Label>
            <Input id="srtHost" {...form.register("srtHost")} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="srtPort">Port</Label>
            <Input id="srtPort" type="number" {...form.register("srtPort")} />
          </div>
          <div className="space-y-2">
            <Label>Mode</Label>
            <Select
              value={form.watch("srtMode") ?? "caller"}
              onValueChange={(v) => form.setValue("srtMode", v)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="caller">Caller</SelectItem>
                <SelectItem value="listener">Listener</SelectItem>
                <SelectItem value="rendezvous">Rendezvous</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="srtLatency">Latency (ms)</Label>
            <Input
              id="srtLatency"
              type="number"
              {...form.register("srtLatency")}
            />
          </div>
        </>
      )}

      {outputType === "youtube_live" && (
        <>
          <div className="space-y-2">
            <Label htmlFor="youtubeRtmpUrl">RTMP URL</Label>
            <Input
              id="youtubeRtmpUrl"
              placeholder="rtmp://a.rtmp.youtube.com/live2"
              {...form.register("youtubeRtmpUrl")}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="youtubeStreamKey">Stream Key</Label>
            <Input
              id="youtubeStreamKey"
              type="password"
              {...form.register("youtubeStreamKey")}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="broadcastId">Broadcast ID</Label>
            <Input
              id="broadcastId"
              placeholder="YouTube broadcast or video ID"
              {...form.register("broadcastId")}
            />
            <p className="text-xs text-muted-foreground">
              Required for live stats. Found in YouTube Studio under your live
              stream.
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="youtubeApiKey">YouTube API Key</Label>
            <Input
              id="youtubeApiKey"
              type="password"
              placeholder="AIza..."
              {...form.register("youtubeApiKey")}
            />
            <p className="text-xs text-muted-foreground">
              Required for live stats. Create one in Google Cloud Console.
            </p>
          </div>
        </>
      )}

      <div className="flex items-center space-x-2">
        <Switch
          id="enabled"
          checked={form.watch("enabled")}
          onCheckedChange={(checked) => form.setValue("enabled", checked)}
        />
        <Label htmlFor="enabled">Enabled</Label>
      </div>

      <div className="flex gap-2 pt-2">
        <Button type="submit" disabled={isLoading}>
          {output ? "Update" : "Create"} Output
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
};
